/**
 * Get the exception message from the $http exception object.
 */
export function getExceptionMessage(ex) {
    // Make a list of patterns to match, in order, on the exception object.
    const patterns = [
        { prop: "data", transform: getExceptionMessage },
        { prop: "message", transform: identity },
        { prop: "InnerException", transform: getInnerExceptionMessage },
        { prop: "Message", transform: identity },
        { prop: "Messages", transform: messages => messages.join(", ") },
        { prop: "statusText", transform: identity },
        {
            prop: "status",
            transform: status => status <= 0
                ? "The connection was reset or blocked"
                : ex
        }
    ]

    // If the object is falsy, return it.
    if (!ex) return ex;
    for (const pattern of patterns) {
        if (ex[pattern.prop] !== undefined) {
            const message = pattern.transform(ex[pattern.prop]);
            if (message) {
                // If the message is empty, skip to the next field.
                return message;
            }
        }
    }
    // if the object doesn't match any patterns, return it directly
    return ex
}

/**
 * Create a new scope with the given args injected, and evaluate the bound string
 * in that child scope.
 * @param {any} scope The parent scope.
 * @param {any} args A generic object of arguments representing scope values to be included.
 * @param {string} evalString Code to be evaluated in the child scope.
 */
export function evalInChildScope(scope, args, evalString) {
    // Don't pass args to $new since creating an isolate scope means changes
    // won't execute in the parent scope as expected.
    const childScope = Object.assign(scope.$new(), args || {});
    return childScope.$eval(evalString);
}

/**
 * Strip the thousands separator from the string number and return it as a number.
 * @param {String} n String to be converted to a number.
 * @returns {Number} The input string converted to a Number.
 */
export function readNumber(n) {
    if (!n) return 0;
    // Strip formatting characters like $, %, thousands separators.
    // Treat left parenthesis as a negative sign and strip right parenthensis, since (number) means -number.
    return Number(n.toString().replace(/[$,%\s)]/g, "").replace("(", "-")) || 0;
}

/**
 * Return whether the given object can be parsed as a finite number.
 * @param {any} n The value being checked.
 * @returns {boolean} Whether the input number is a numeric value.
 */
export function isNumeric(n) {
    const strippedNumeric = n.toString().replace(/[$,%\s)]/g, "").replace("(", "-");
    const convertedNumeric = parseFloat(strippedNumeric);
    // The regex isn't nearly comprehensive, but is a quick way to disqualify leftover non-numeric characters.
    return /^[\d\-.]+$/.test(strippedNumeric) && !isNaN(convertedNumeric) && isFinite(convertedNumeric);
}

/**
 * Drop a certain number of stack frames from the given stack trace.
 * @param stack The stack trace whose top n frames will be dropped.
 * @param n The number of frames to drop.
 */
export function dropStackFrames(stack, n) {
    // Frames typically come in two formats: Error preamble at the top, followed by "    at ..."
    // or no preamble with an ampersand between the function name and the location.
    const framePredicate = p => /@http/.test(p) || /\s+at/.test(p);
    const frames = stack.split("\n");
    const topIndex = frames.findIndex(framePredicate);
    const newTopIndex = Math.min(topIndex + n, frames.length);
    return frames.slice(0, topIndex).concat(frames.slice(newTopIndex)).join("\n");
}

/**
 * A function that returns its parameter.
 * @param {any} i The value to be returned
 * @returns {any} The object i by value.
 */
function identity(i) {
    return i;
}

/**
 * Get the exception message for the .NET exception instance or
 * the message for its innermost InnerException.
 * @param {import("./data-utils").DotNetException} ex A .NET System.Exception instance.
 */
function getInnerExceptionMessage(ex) {
    return ex.InnerException ? getInnerExceptionMessage(ex.InnerException)
        : ex.ExceptionMessage || ex.Message;
}
